import BaseObject, { BaseObjectParams } from './baseObject';
import Game from './game';
import LevelScene, { LevelSceneParams } from './scene/LevelScene';
import Ship, { ShipParams } from './element/ship/ship';
import Asteroid from './element/hazard/asteroid';
import PatrolOrder from './order/patrolOrder';
import { SpriteParams } from './sprite/sprite';
import { DoubleMusicParams } from './audio/doubleMusic';
import { MusicParams } from './audio/music';

import { randomInArray, randomIndexInArray } from '../utils/array';
import { Faction } from '../utils/faction';
import { CoordsType, getDistanceBy2XY } from '../utils/geometry';

import * as shipConfig from '../config/ships.config';
import * as levelConfig from '../config/levels.config'

enum randomEventCategory {
  ENEMY,
  ASTEROID,
}

enum randomEventLevel {
  LOW,
  MEDIUM,
  HIGH,
  VERY_HIGH,
}

interface weightedEventObject<T> {
  [key: number]: T;
}

type randomEventType = {
  category: randomEventCategory;
  level: randomEventLevel;
}

export interface LevelParams extends BaseObjectParams {
  size?: keyof typeof levelConfig.sizes;
  width?: number;
  height?: number;
  hasWarFog?: boolean;
  backgroundSpriteParams?: Partial<SpriteParams>;
  musicsParamsArray?: Partial<MusicParams | DoubleMusicParams>[];
  onInit?: (level: Level) => void;
}

export default class Level extends BaseObject implements LevelParams {
  size?: keyof typeof levelConfig.sizes;
  width!: number;
  height!: number;
  hasWarFog?: boolean;
  backgroundSpriteParams?: Partial<SpriteParams>;
  musicsParamsArray?: Partial<MusicParams | DoubleMusicParams>[];
  onInit?: (level: Level) => void;

  description: Record<string, any> = {};

  static getRandomSize() {
    return randomInArray(Object.keys(levelConfig.sizes));
  }

  static getRandomBackground() {
    return randomInArray(Object.values(levelConfig.backgrounds));
  }

  static getRandomMusic() {
    return randomInArray(Object.values(levelConfig.musics));
  }

  get scene() {
    return Game.instance.scene as LevelScene;
  }

  constructor(params: LevelParams) {
    super();
    this.applyParams(params);

    if (!this.width && !this.height) {
      if (!this.size) {
        this.size = Level.getRandomSize();
        this.description['size'] = this.size;
      }
      const sizeKey = this.size as keyof typeof levelConfig.sizes; // Ensure this.size is defined before using it as an index
      this.width = levelConfig.sizes[sizeKey].width;
      this.height = levelConfig.sizes[sizeKey].height;
    }

    if (this.hasWarFog === undefined) {
      this.hasWarFog = randomInArray([true, false]);
    }
    this.description['War Fog'] = `${this.hasWarFog ? 'Yes' : 'No'}`;

    if (!this.backgroundSpriteParams) {
      this.backgroundSpriteParams = Level.getRandomBackground();
    }

    if (!this.musicsParamsArray) {
      this.musicsParamsArray = Level.getRandomMusic();
    }
  }

  start() {
    Game.instance.level = this;
    this.init();
    this.populateScene();
  }

  /* init components for a new scene */
  init(): void {
    const { width, height, hasWarFog, backgroundSpriteParams, musicsParamsArray } = this;

    /* Creates the whole scene and all components attached to it */
    Game.instance.scene = new LevelScene({
      width,
      height,
      hasWarFog,
      backgroundSpriteParams,
      musicsParamsArray,
    } as LevelSceneParams).create();
    if (this.onInit)
      this.onInit(this);
  }

  /* Procedurally add resources, enemies and encounters into the map */
  populateScene(): void {
    function fillArrayByWeightedObject(weightedObject: weightedEventObject<number>) {
      return Object.entries(weightedObject)
        .reduce<number[]>((acc, cur) => {
          const [key, val] = cur;
          for (let i = 0; i < val; i++) {
            acc.push(Number(key));
          }
          return acc;
        }, [])
        ;
    }

    /* Generate a pool of possible random events to be inserted in the scene */
    const weightedEventCategories = {
      [randomEventCategory.ENEMY]: 5,
      [randomEventCategory.ASTEROID]: 2,
    };
    const weightedEventLevels = {
      [randomEventLevel.LOW]: 10,
      [randomEventLevel.MEDIUM]: 5,
      [randomEventLevel.HIGH]: 3,
      [randomEventLevel.VERY_HIGH]: 1,
    };
    const allCategoriesArray = fillArrayByWeightedObject(weightedEventCategories);
    const allLevelsArray = fillArrayByWeightedObject(weightedEventLevels);

    const { scene } = this;
    const {
      mainCanvas: mainCtx,
    } = Game.instance.contexts;
    const additionalShipParams = { scene, context: mainCtx };

    const eventsCoords: CoordsType[] = [];

    const numbersOfEvents = Math.round(this.width / Game.instance.canvasWidth);
    const minDistanceFromEdge = Game.instance.canvasWidth; // events should not be too close to the edges of the map
    const minDistanceFromOtherEvents = Game.instance.canvasWidth; // events should not overlap below a certain distance from each others

    let retries = 0;
    const retriesLimit = 100;
    for (let i = 0; i < numbersOfEvents; i++) {
      const categoryId = randomIndexInArray(allCategoriesArray);
      const levelId = randomIndexInArray(allLevelsArray);
      const category = allCategoriesArray[categoryId];
      const level = allLevelsArray[levelId];
      const event: randomEventType = {
        category,
        level,
      };

      const x = Math.random() * (this.scene.width - minDistanceFromEdge * 2) + minDistanceFromEdge;
      const y = Math.random() * (this.scene.height - minDistanceFromEdge * 2) + minDistanceFromEdge;

      /* retry this event generation if too close from another */
      const closestDistanceFromOtherEvents = eventsCoords.reduce((acc, cur) => {
        const distanceFromEventCenter = getDistanceBy2XY({
          x1: x, y1: y,
          x2: cur.x, y2: cur.y,
        });
        return Math.min(acc, distanceFromEventCenter);
      }, Infinity);
      if (closestDistanceFromOtherEvents < minDistanceFromOtherEvents) // reset step if too close to other events
      {
        i--;
        retries++;
        if (retries >= retriesLimit)
          break;
        continue;
      }

      /* remove random elements that have been picked from the global arrays */
      // allCategoriesArray.splice(categoryId, 1);
      // allLevelsArray.splice(levelId, 1);

      switch (event.category) {
        case randomEventCategory.ENEMY:
          {
            const patrolDistance = 200;
            const patrolDestination = {
              x: x + Math.ceil(Math.random() * patrolDistance) * randomInArray([1, -1]),
              y: y + Math.ceil(Math.random() * patrolDistance) * randomInArray([1, -1]),
            };
            for (let j = 0; j < event.level + 1; j++) {
              const enemy = new Ship({
                ...shipConfig.basicJet,
                ...additionalShipParams,
                faction: Faction.ENEMY,
                x: x - 50 * (j - 1),
                y: y,
              } as ShipParams).create();
              enemy.behaviour.setOrder(new PatrolOrder({
                x: patrolDestination.x,
                y: patrolDestination.y,
              }));
            }
          }
          break;

        case randomEventCategory.ASTEROID:
          Asteroid.generateAsteroids({
            scene: this.scene,
            context: Game.instance.contexts.mainCanvas,
            number: [5, 10, 20, 40][event.level],
            centerX: x,
            centerY: y,
            maxDistance: [150, 300, 600, 1200][event.level],
            moveAngle: 270,
            canStack: false,
          });
          break;
      }
      eventsCoords.push({ x, y }); // store coords for comparisons with next ones
    }
  }

  end() {
    this.scene.reset();

    Game.instance.controlSchemes.rts.disable();
    Game.instance.scene = Game.instance.levelSelector!;
    Game.instance.levelSelector!.music.play();

    // re-center cursor
    Game.instance.cursor.x = Game.instance.scene.width / 2;
    Game.instance.cursor.y = Game.instance.scene.height / 2;
  }
}
